export const SECTION_NAME = "Adjustment";

export const INTRO_TEXT = `Inventory adjustments allow you to modify the
  quantity and/or cost basis of inventory on hand. You should use inventory
  adjustments sparingly, as most often inventory will be added or removed
  through item receipts and shipments.`;

export const EXAMPLE_JSON = `{ 
  "id":853,
  "starred":0,
  "syncToken":0,
  "number":"",
  "date":"2019-10-09T15:29:07",
  "total":1,
  "account":{ 
    "id":47,
    "name":"Inventory Shrinkage"
  },
  "location":{ 
    "id":35,
    "name":"Warehouse"
  },
  "department":null,
  "comment":"",
  "customFields":null,
  "archived":false,
  "summaryOnly":false,
  "hasSignature":false,
  "lines":[ 
    { 
      "id":12,
      "linenumber":0,
      "item":{ 
        "id":200,
        "name":"Widget"
      },
      "class":null,
      "job":null,
      "workcenter":null,
      "lot":null,
      "bin":null,
      "description":null,
      "quantityDiff":-1.00000,
      "newQuantity":0.0,
      "valueDiff":1,
      "uom":null,
      "serials":[]
    }
  ]
}`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. Must not be provided on create transactions.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The adjustment number for this record.  If you wish to use the automatic numbering capability 
    on creation of an adjustment, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "total",
    type: "number",
    description: "Total cost basis for this adjustment.",
  },
  {
    name: "account",
    type: "reference",
    description: `The adjustment account used for this transaction.`,
  },
  {
    name: "location",
    type: "reference",
    description: `Location for this transaction.`,
  },
  {
    name: "department",
    type: "reference",
    description: `The department for this transaction.`,
  },
  {
    name: "comment",
    type: "string",
    description:
      "The company’s internal comment about this transaction. This comment is not visible to the customer.",
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `True if the summary parameter was set when this record was retrieved. False if not.`,
    readOnly: true,
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
    readOnly: true,
  },
  {
    name: "lines",
    type: "array of adjustment line items",
    description: `The lines for the adjustment.  See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this adjustment line item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on the adjustment transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },

  {
    name: "description",
    type: "string",
    description: `The item description.`,
  },
  {
    name: "quantityDiff",
    type: "decimal",
    description: `The adjustment amount for this line.`,
  },
  {
    name: "newQuantity",
    type: "decimal",
    description: `The new quantity for this item.`,
  },
  {
    name: "valueDiff",
    type: "decimal",
    description: `The cost basis adjustment for this line.`,
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line.`,
  },
  {
    name: "bin",
    type: "reference",
    description: `The bin used for this item adjustment.`,
  },
  {
    name: "lot",
    type: "reference",
    description: `The lot used for this item adjustment.`,
  },
  {
    name: "serials",
    type: "array of references",
    description: `The serials being adjusted for this item.  You cannot add serials to inventory with adjustments.`,
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the estimate will be returned.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, id, comment, or location name.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "status",
    type: "open/closed",
    description: `Filters the results by whether the transaction is open or closed.`,
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `,
  },
  {
    name: "location",
    type: "string",
    description: `Filters transactions according to the name of the location.`,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/adjustment?start=101&maxresults=100&status=open&from=2019-09-01T00:00:00&to=2019-09-10T00:00:00&location=warehouse&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
